import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Item, Section } from 'react-stately';
import { coerce, compare } from 'semver';
import {
  IconVersionOutline,
} from '@saucelabs/ds-icons';
import { Filter } from '@saucelabs/ds-filters';

import actions from '../../actions';
import constants from '../../constants';
import selectors from '../../selectors';
import { trackEvent, MANUAL_EVENTS } from '../../../../tracking';

type GroupedVersion = {
  majorVersion: number,
  subVersions: string[],
}

const {
  MOBILE_SELECTION_FILTER_CATEGORY: FILTER_CATEGORY,
} = constants;

const OsVersionsFilter: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    activeFilters,
    currentFacets,
    testType,
  } = useSelector(selectors.mobileSelectionSelector);

  let facetsForActivePlatform = currentFacets?.ALL || {};

  // only show version options for the selected platform if one is selected
  const activePlatformFilterValues = activeFilters.get(FILTER_CATEGORY.OS);
  if (activePlatformFilterValues.size === 1) {
    facetsForActivePlatform = currentFacets?.[activePlatformFilterValues.first()] || {};
  }

  const availableVersions = facetsForActivePlatform?.[FILTER_CATEGORY.OS_VERSION] || [];

  const availableVersionGroupedByMajor = useMemo(() => {
    const groupedVersions: GroupedVersion[] = [];
    availableVersions.forEach((version) => {
      const semanticVersion = coerce(version);

      if (semanticVersion?.major) {
        const majorVersion = semanticVersion.major;
        const majorVersionGroupIndex = groupedVersions.findIndex(
          (group) => group.majorVersion === majorVersion,
        );

        if (majorVersionGroupIndex >= 0) {
          groupedVersions[majorVersionGroupIndex].subVersions.push(version);
        } else {
          groupedVersions.push({
            majorVersion,
            subVersions: [version],
          });
        }
      }
    });

    return groupedVersions.sort(
      (groupA, groupB) => {
        const groupASemver = coerce(groupA.majorVersion);
        const groupBSemver = coerce(groupB.majorVersion);

        if (groupASemver && groupBSemver) {
          return compare(groupBSemver, groupASemver);
        }
        return 0;
      });
  }, [availableVersions]);

  const activeOsVersionFilterValues = activeFilters.get(FILTER_CATEGORY.OS_VERSION);

  return (
    <Filter
      data-qa="device-filter-bar-os-versions"
      label="All Versions"
      name="osVersions"
      onSelectionChange={(newValues) => {
        dispatch(
          actions.setFilters({
            category: FILTER_CATEGORY.OS_VERSION,
            values: [...newValues],
          }));
        trackEvent(MANUAL_EVENTS.FILTER_REAL_DEVICE, {
          filterCategory: FILTER_CATEGORY.OS_VERSION,
          filterValues: [...newValues],
          testType,
        });
      }}
      prefixIcon={IconVersionOutline}
      selectedKeys={activeOsVersionFilterValues}
      selectionMode="multiple"
      type="listbox"
    >
      {availableVersionGroupedByMajor.map((group) => {
        if (
          group.subVersions.length === 1 &&
          group.majorVersion.toString() === group.subVersions[0]
        ) {
          // Don't add a section if the only subVersion is equal to the major
          return (
            <Item
              key={group.subVersions[0]}
              textValue={group.subVersions[0]}
            >
              {group.subVersions[0]}
            </Item>
          );
        }
        return (
          <Section
            title={group.majorVersion}
          >
            {group.subVersions.map((subVersion) => (
              <Item
                key={subVersion}
                textValue={subVersion}
              >
                {subVersion}
              </Item>
            ))}
          </Section>
        );
      })}
    </Filter>
  );
};

export default OsVersionsFilter;
